// frontend/src/farm/vehicles.mjs
// What the vehicle UI says and shows (spec 2026-09-28-farm-3-vehicles-design.md §2): the tool
// strip's buttons, the hover hint, the float after an area, the fuel chip. Pure (node --test).
export const TOOLS = ['tractor', 'seeder', 'harvester'];
export const ACTION_FOR_TOOL = { tractor: 'plow', seeder: 'plant', harvester: 'harvest' };
export const WORK_LABEL = { plow: 'Orka', plant: 'Siew', harvest: 'Zbiór' };
export const VEHICLE_DURATION_MS = 1200;
export const vehicleKey = (tool, size) => `farm_vehicle_${tool}_${size}`;

export function plural(n, one, few, many) {
  if (n === 1) return one;
  const d = n % 10, t = n % 100;
  return d >= 2 && d <= 4 && (t < 12 || t > 14) ? few : many;
}

export function toolButtons(raw) {
  const cat = raw?.catalog?.farm?.tools ?? {};
  const owned = raw?.farm?.tools ?? {};
  const level = raw?.level ?? 1;
  return [
    { id: 'hand', label: '{hand} Ręka', size: 1, enabled: true, lockLevel: null, buyable: false },
    // Zasyp (owner 2026-09-29): empty fields back to grass — a hand tool, never needs fuel
    { id: 'recycle', label: '{shovel} Zasyp', size: 1, enabled: true, lockLevel: null, buyable: false },
    ...TOOLS.filter((id) => cat[id]).map((id) => {
      const size = owned[id] ?? 0;
      const lockLevel = size > 0 ? null : cat[id].tiers[0].level;
      // unowned but unlocked: the strip says "Kup" (tapping opens Narzędzia), not a padlock
      return { id, label: cat[id].name_pl, size, enabled: size > 0, lockLevel, buyable: lockLevel !== null && level >= lockLevel };
    }),
  ];
}

// The picture on a machine's tile (owner 2026-10-02): always the machine itself — the size it has, or its first
// size while not yet bought (a padlocked Siewnik used to show a seedling, which read as a seed). Hand tools: none.
export function toolArtKey(b, raw) {
  if (!TOOLS.includes(b.id)) return null;
  const first = raw?.catalog?.farm?.tools?.[b.id]?.tiers?.[0]?.size ?? 2;
  return vehicleKey(b.id, b.size > 0 ? b.size : first);
}

// Every machine's first-size picture: the strip draws them even before they are bought.
export const firstVehicleKeys = (raw) => TOOLS.filter((id) => raw?.catalog?.farm?.tools?.[id])
  .map((id) => vehicleKey(id, raw.catalog.farm.tools[id].tiers?.[0]?.size ?? 2));

// The seed the Siewnik will plant, as a small badge on its tile — shown whether it is owned yet or not.
// `color`: a non-start flower colour, or null (Kwiaty §7) — the same family as farmRules.cropArtId.
export const toolSeedBadge = (b, seed, color = null) => (b.id === 'seeder' && seed ? `farm_crop_${color ? `${seed}_${color}` : seed}_ready` : null);

export function areaHint(action, n, { seedName = '', price = 0 } = {}) {
  if (n === 0) return 'Tu nie ma nic do zrobienia';
  if (action === 'plow') return `Zaorze ${n} ${plural(n, 'pole', 'pola', 'pól')} · ${n * price} {coin} · ${n} {fuel}`;
  if (action === 'plant') return `Posadzi ${n} × ${seedName} · ${n * price} {coin} · ${n} {fuel}`;
  return `Zbierze ${n} ${plural(n, 'pole', 'pola', 'pól')} · ${n} {fuel}`;
}

const signed = (v) => (v > 0 ? `+${v}` : `−${-v}`);

export function workFloat({ money = 0, fuel = 0, xp = 0 }) {
  const parts = [];
  if (money) parts.push(`${signed(money)} {coin}`);
  if (fuel) parts.push(`${signed(fuel)} {fuel}`);
  if (xp) parts.push(`${signed(xp)} XP`);
  return parts.join('  ');
}

// A friend's spare canister is offered before the paid refill (spec 2026-09-29-farm-fuel-gifts).
// Out of fuel mid-queue: the tank fills itself (owner 2026-10-16) — a friend's canister first,
// then a paid refill; null when neither is possible (the strip shows its Brak monet pause).
export function autoRefill({ cans = 0, money = 0, cfg }) {
  if (cans > 0) return { kind: 'can', toast: 'Zatankowano z kanistra od znajomych {canister}' };
  if (money >= cfg.refill_cost) return { kind: 'buy', toast: `Zatankowano za ${cfg.refill_cost} {coin}` };
  return null;
}

export function fuelChip(units, { paused = false, money = 0, cfg, cans = 0 }) {
  const text = `{fuel} ${Math.floor(units)}${cans > 0 ? ` · {canister} ${cans}` : ''}`;
  if (!paused) return { text, alarm: false, refill: null };
  if (cans > 0) return { text, alarm: true, refill: { label: `Użyj kanistra (${cans})`, enabled: true, kind: 'can' } };
  const enabled = money >= cfg.refill_cost;
  return { text, alarm: true, refill: { label: enabled ? `Zatankuj — ${cfg.refill_cost} {coin}` : 'Brak monet', enabled, kind: 'buy' } };
}
